export default class ArcgisAuthenticate {
  private clientId = process.env.VUE_APP_TOKEN_SERVICE_CLIENT_ID;
  private authUrl = process.env.VUE_APP_TOKEN_SERVICE_AUTH_URL;
  private expiration = process.env.VUE_APP_TOKEN_SERVICE_EXPIRATION;

  public getItem = (key: string): string => {
    return window.localStorage.getItem(key) as string;
  };

  public setItem = (key: string, value: string): void => {
    return window.localStorage.setItem(key, value);
  };

  public removeItem = (key: string): void => {
    return window.localStorage.removeItem(key);
  };

  public setCookie = (cookie: string, value: string): void => {
    document.cookie = `${cookie}=${value};path=/`;
  };

  public getCookie = (cookie: string): string => {
    return (
      document.cookie
        .split(";")
        .filter((item: string) => {
          return item.trim().indexOf(`${cookie}=`) != -1;
        })
        .pop() || "cookie="
    ).split("=")[1];
  };

  public hasCookie = (cookie: string): boolean => {
    return document.cookie.split(";").some((item: string) => {
      return item.trim().indexOf(`${cookie}=`) != -1;
    });
  };

  private removeCookie = (cookie: string): void => {
    document.cookie
      .split(";")
      .filter((item: string) => {
        return item.trim().indexOf(`${cookie}=`) != -1;
      })
      .forEach((item: string) => {
        document.cookie = item + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
      });
  };

  private redirectUrl(encodedRedirectUri: string) {
    window.location.href = `${this.authUrl}?client_id=${this.clientId}&redirect_uri=${encodedRedirectUri}&response_type=code&expiration=${this.expiration}`;
  }

  private getRetryCount() {
    return parseInt(this.getItem("retry_count"));
  }

  private setRetryCount(count: number) {
    this.setItem("retry_count", count.toString());
  }

  public decreaseRetryCount() {
    let retryCount: number = this.getRetryCount();
    retryCount--;
    this.setRetryCount(retryCount);
  }

  public resetRetryCount() {
    this.setRetryCount(2);
  }

  public authenticate = (authCode: string | null, redirectUri: string) => {
    const encodedRedirectUri = encodeURIComponent(redirectUri);
    if (!this.getItem("retry_count")) this.setRetryCount(2);
    if (authCode) {
      // else we get 'auth_code' back from ArcGIS auth url and put it in storage for the next API call
      this.setItem("auth_code", authCode);
      this.setItem("redirect_uri", encodedRedirectUri);
    } else if (this.hasCookie("arcgis_enterprise_auth")) {
      // we have an authorisation cookie so we don't need to re-authenticate
      // clear auth_code from localStorage if is exists
      if (this.getItem("auth_code")) this.removeItem("auth_code");
    } else if (this.getRetryCount() > 0) {
      // we haven't already tried to authenticate, so authenticating now
      this.decreaseRetryCount();
      // go to the auth url to get a code
      this.redirectUrl(encodedRedirectUri);
    } else {
      this.setRetryCount(2);
      this.removeCookie("arcgis_enterprise_auth");
      throw "Authentication failed";
    }
  };

  public reauthenticate = () => {
    this.removeCookie("arcgis_enterprise_auth");
    if (this.getRetryCount() > 0) {
      const encodedRedirectUri = this.getItem("redirect_uri");
      this.decreaseRetryCount();
      this.redirectUrl(encodedRedirectUri);
    } else {
      this.setRetryCount(2);
      throw "Authentication failed";
    }
  };
}
